import type { PushAnalyticsResponse, PushAnalyticsRow, PushMetricDefinition, PushMetricKey } from '../../../api/client.ts';
import { formatPushMetric, PushMetricTooltip } from './MetricTooltip.tsx';

/** Columns as in Braze Message Performance [41]: each count with its rate under it. */
const COLUMNS: ReadonlyArray<{ count: PushMetricKey; rate?: PushMetricKey }> = [
  { count: 'sends' },
  { count: 'accepted', rate: 'accepted_rate' },
  { count: 'bounces', rate: 'bounce_rate' },
  { count: 'received', rate: 'received_rate' },
  { count: 'direct_opens', rate: 'direct_open_rate' },
  { count: 'influenced_opens', rate: 'influenced_open_rate' },
  { count: 'total_opens', rate: 'total_open_rate' },
  { count: 'body_clicks' },
  { count: 'unique_recipients' },
];

export const PLATFORM_LABELS: Record<string, string> = { android: 'Android', ios: 'iOS', web: 'Web', unknown: 'Unknown platform' };

/** The row's group as people say it: platform name, day, or variant (names from the message when the page has them). */
export function pushRowLabel(row: PushAnalyticsRow, groupBy: PushAnalyticsResponse['groupBy'], variantLabels: Record<string, string> = {}): string {
  if (groupBy === 'platform') return PLATFORM_LABELS[row.key.platform ?? 'unknown'] ?? row.key.platform ?? 'Unknown platform';
  if (groupBy === 'day') return row.key.day ?? '—';
  return row.key.variant ? (variantLabels[row.key.variant] ?? row.key.variant) : 'No variant';
}

function Cell({ row, count, rate, defs }: { row: PushAnalyticsRow; count: PushMetricKey; rate?: PushMetricKey | undefined; defs: Map<PushMetricKey, PushMetricDefinition> }) {
  const rateDef = rate ? defs.get(rate) : undefined;
  return (
    <td>
      <span className="push-table__count">{formatPushMetric('count', row.metrics[count] ?? null)}</span>
      {rate && rateDef && (
        <span className="push-table__rate" title={row.metrics[rate] === null ? `${rateDef.title.en}: no denominator in this period` : undefined}>
          {formatPushMetric('percent', row.metrics[rate] ?? null)}
        </span>
      )}
    </td>
  );
}

/**
 * Push performance per delivery (BB-79): one row per platform, day or variant and a total, with Sends, Accepted by
 * provider, Bounces, Received on device, Direct / Influenced / Total opens, Body clicks and Unique recipients. Every
 * header opens the metric's definition from the API (`definitions`), so the tooltip and the number come from one
 * registry; taps per button follow in their own table.
 */
export function PushPerformanceTable({ data, variantLabels, buttonLabels = {} }: { data: PushAnalyticsResponse; variantLabels?: Record<string, string>; buttonLabels?: Record<string, string> }) {
  const defs = new Map(data.definitions.map((d) => [d.key, d]));
  const columns = COLUMNS.filter((c) => defs.has(c.count));
  const label = (row: PushAnalyticsRow) => pushRowLabel(row, data.groupBy, variantLabels);
  const buttons = Object.keys(data.total.counts.buttonClicks).sort();
  const groupHeader = data.groupBy === 'platform' ? 'Platform' : data.groupBy === 'day' ? 'Day' : 'Variant';
  return (
    <>
      <div className="push-table-scroll">
        <table className="table push-table" aria-label="Push performance">
          <thead>
            <tr>
              <th scope="col">{groupHeader}</th>
              {columns.map((c) => {
                const def = defs.get(c.count)!;
                const rateDef = c.rate ? defs.get(c.rate) : undefined;
                return (
                  <th key={c.count} scope="col">
                    <PushMetricTooltip definition={def} windowMinutes={data.influencedOpenWindowMinutes}>
                      <span>{def.title.en}</span>
                    </PushMetricTooltip>
                    {rateDef && (
                      <span className="push-table__rate">
                        <PushMetricTooltip definition={rateDef} windowMinutes={data.influencedOpenWindowMinutes}>
                          <span>{rateDef.title.en}</span>
                        </PushMetricTooltip>
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <tr key={label(row)}>
                <th scope="row">{label(row)}</th>
                {columns.map((c) => (
                  <Cell key={c.count} row={row} count={c.count} rate={c.rate} defs={defs} />
                ))}
              </tr>
            ))}
          </tbody>
          {data.rows.length > 1 && (
            <tfoot>
              <tr>
                <th scope="row">Total</th>
                {columns.map((c) => (
                  <Cell key={c.count} row={data.total} count={c.count} rate={c.rate} defs={defs} />
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {buttons.length > 0 && (
        <div className="push-table-scroll">
          <table className="table push-table" aria-label="Button clicks">
            <caption className="hint">Taps per notification button (dismissive buttons included; they are not opens).</caption>
            <thead>
              <tr>
                <th scope="col">Button</th>
                {data.rows.map((row) => (
                  <th key={label(row)} scope="col">
                    {label(row)}
                  </th>
                ))}
                {data.rows.length > 1 && <th scope="col">Total</th>}
              </tr>
            </thead>
            <tbody>
              {buttons.map((id) => (
                <tr key={id}>
                  <th scope="row">{buttonLabels[id] ?? id}</th>
                  {data.rows.map((row) => (
                    <td key={label(row)}>{(row.counts.buttonClicks[id] ?? 0).toLocaleString('en-US')}</td>
                  ))}
                  {data.rows.length > 1 && <td>{(data.total.counts.buttonClicks[id] ?? 0).toLocaleString('en-US')}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
